import React, { useState, useRef, useEffect } from 'react';
import { Play, Loader2 } from 'lucide-react';

function Playground({ activeCourse = 'Python', onRunCode }) {
  const getDefaultCode = (course) => {
    if (course === 'Python') return 'print("Hallo Welt")\n# Schreib hier deinen Code!';
    if (course === 'HTML') return '<h1>Hallo Welt</h1>\n<p>Das ist meine erste Webseite.</p>';
    if (course === 'Kotlin') return 'fun main() {\n    println("Hallo Welt")\n    // Schreib hier deinen Code!\n}';
    return 'console.log("Hallo Welt");\n// Schreib hier deinen Code!';
  };

  const [code, setCode] = useState(getDefaultCode(activeCourse));
  const [output, setOutput] = useState('');
  const [pyodide, setPyodide] = useState(null);
  const [isPyodideLoading, setIsPyodideLoading] = useState(activeCourse === 'Python');
  const [isRunning, setIsRunning] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef(null);
  const preRef = useRef(null);

  useEffect(() => {
    setCode(getDefaultCode(activeCourse));
    setOutput('');
  }, [activeCourse]);

  useEffect(() => {
    if (isFocused) {
      document.body.classList.add('keyboard-open');
    } else {
      document.body.classList.remove('keyboard-open');
    }
    return () => document.body.classList.remove('keyboard-open');
  }, [isFocused]);

  useEffect(() => {
    if (activeCourse !== 'Python') {
      setIsPyodideLoading(false);
      return;
    }

    const loadPyodide = async () => {
      if (window.pyodideInstance) {
        setPyodide(window.pyodideInstance);
        setIsPyodideLoading(false);
        return;
      }
      setIsPyodideLoading(true);

      const script = document.createElement('script');
      script.src = "https://cdn.jsdelivr.net/pyodide/v0.25.0/full/pyodide.js";
      script.async = true;
      script.onload = async () => {
        try {
          const py = await window.loadPyodide();
          window.pyodideInstance = py;
          setPyodide(py);
        } catch (e) {
          console.error("Pyodide loading failed", e);
          setOutput("Fehler beim Laden von Python: " + e.message);
        } finally {
          setIsPyodideLoading(false);
        }
      };
      document.body.appendChild(script);
    };
    loadPyodide();
  }, [activeCourse]);

  const highlightCode = (code) => {
    // Einfacher Tokenizer für Syntax Highlighting
    // Erweitert für JS: Backticks, Kommentare (// und #), mehr Operatoren/Klammern
    const tokens = code.split(/(".*?"|'.*?'|`.*?`|\/\/.*|#.*|\b\d+\b|[()=+\-*/.,;:{}[\]!&|<>]|\s+)/g);
    return tokens.map((token, i) => {
      if (!token) return null;
      let color = '#abb2bf'; // default
      const trimmed = token.trim();

      if (/^".*"$|^'.*'$|^`.*`$/.test(token)) color = '#98c379'; // string
      else if (/^\d+$/.test(token)) color = '#d19a66'; // number
      else if (token.startsWith('//') || token.startsWith('#')) color = '#5c6370'; // comment
      else if (['(', ')', '[', ']', '{', '}', '=', '+', '-', '*', '/', '.', ',', ';', ':', '!', '&', '|', '<', '>'].includes(trimmed)) color = '#56b6c2'; // operator
      else {
        if (activeCourse === 'Python') {
           if (['print', 'if', 'else', 'for', 'while', 'def', 'return', 'import', 'from', 'class', 'try', 'except', 'True', 'False', 'None', 'and', 'or', 'not', 'in', 'is'].includes(trimmed)) color = '#c678dd';
      } else if (activeCourse === 'Kotlin') {
         if (['fun', 'val', 'var', 'println', 'print', 'if', 'else', 'for', 'while', 'class', 'return', 'import', 'true', 'false', 'null', 'String', 'Int', 'Boolean'].includes(trimmed)) color = '#c678dd';
        } else if (activeCourse === 'HTML') {
           const tags = ['html', 'head', 'body', 'div', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'a', 'img', 'ul', 'li', 'ol', 'button', 'input', 'form', 'label', 'br', 'hr', 'strong', 'em', 'b', 'i', 'script', 'style', 'link', 'meta', 'title', 'doctype', 'table', 'tr', 'td', 'th', 'thead', 'tbody', 'footer', 'header', 'nav', 'section', 'article', 'aside', 'main', 'canvas', 'video', 'audio', 'source', 'iframe', 'blockquote', 'code', 'pre'];
           const attributes = ['class', 'id', 'src', 'href', 'style', 'type', 'value', 'placeholder', 'alt', 'width', 'height', 'rel', 'lang', 'target', 'disabled', 'checked', 'selected', 'readonly', 'required', 'action', 'method', 'name', 'for', 'rows', 'cols', 'charset', 'content', 'http-equiv'];
           
           if (tags.includes(trimmed.toLowerCase())) color = '#e06c75'; // Tags (Red/Pink)
           else if (attributes.includes(trimmed.toLowerCase())) color = '#d19a66'; // Attributes (Orange)
        } else {
           if (['console', 'log', 'if', 'else', 'for', 'while', 'function', 'return', 'const', 'let', 'var', 'true', 'false', 'null', 'undefined', 'class', 'new', 'this', 'import', 'from', 'async', 'await', 'try', 'catch', 'switch', 'case', 'break', 'default'].includes(trimmed)) color = '#c678dd';
        }
      }
      return <span key={i} style={{ color }}>{token}</span>;
    });
  };

  const runCode = async () => {
    if (navigator.vibrate) navigator.vibrate(10);
    if (onRunCode) onRunCode();

    if (activeCourse === 'HTML') {
      setIsRunning(true);
      setOutput(code);
      setIsRunning(false);
      return;
    }

    if (activeCourse === 'Kotlin') {
      setIsRunning(true);
      setOutput("Führe aus...");
      setTimeout(() => {
        try {
          // Einfacher Mock-Compiler für Kotlin (simuliert Konsolenausgaben für den Playground)
          const out = [];
          const lines = code.split('\n');
          lines.forEach(line => {
            const trimmed = line.trim();
            if (trimmed.startsWith('println(') || trimmed.startsWith('print(')) {
              const match = trimmed.match(/print(ln)?\s*\(\s*(.*?)\s*\)/);
              if (match) {
                let val = match[2];
                if ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'"))) {
                  val = val.substring(1, val.length - 1);
                }
                out.push(val);
              }
            }
          });
          setOutput(out.length ? out.join('\n') : "Code ausgeführt (keine Ausgabe)");
        } catch (e) {
          setOutput(String(e));
        } finally {
          setIsRunning(false);
        }
      }, 600);
      return;
    }

    if (activeCourse === 'JavaScript') {
      setIsRunning(true);
      setOutput("Führe aus...");
      const logs = [];
      const originalLog = console.log;
      const originalError = console.error;
      console.log = (...args) => logs.push(args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(' '));
      console.error = (...args) => logs.push("Error: " + args.map(a => String(a)).join(' '));
      
      try {
        // eslint-disable-next-line no-new-func
        new Function(code)();
        setOutput(logs.length ? logs.join('\n') : "Code ausgeführt (keine Ausgabe)");
      } catch (e) {
        setOutput(String(e));
      } finally {
        console.log = originalLog;
        console.error = originalError;
        setIsRunning(false);
      }
      return;
    }

    if (!pyodide) return;
    setIsRunning(true);
    setOutput("Führe aus...");

    try {
      // Capture stdout
      await pyodide.runPythonAsync(`
import sys
import io
sys.stdout = io.StringIO()
`);
      await pyodide.runPythonAsync(code);
      const stdout = pyodide.runPython("sys.stdout.getvalue()");
      setOutput(stdout || "Code ausgeführt (keine Ausgabe)");
    } catch (error) {
      setOutput(String(error));
    } finally {
      setIsRunning(false);
    }
  };

  const insertChar = (char) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newCode = code.substring(0, start) + char + code.substring(end);
    
    setCode(newCode);
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + char.length, start + char.length);
    }, 0);
  };

  const handleScroll = (e) => {
    if (preRef.current) {
      preRef.current.scrollTop = e.target.scrollTop;
      preRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  return (
    <main className="main-content" style={{ height: '100vh', overflowY: 'auto', position: 'relative', zIndex: 1 }}>
      <style>{`
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
      <style>{`
        .spin { animation: loader-spin 1s linear infinite; }
        @keyframes loader-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
      <style>{`
        .code-toolbar {
          display: flex;
          gap: 8px;
          padding: 10px;
          background: #252526;
          overflow-x: auto;
          border-top: 1px solid #333;
          justify-content: center;
        }
        @media (max-width: 768px) {
          .code-toolbar {
            display: none;
            position: fixed;
            bottom: 0;
            left: 0;
            width: 100%;
            z-index: 9999;
            box-shadow: 0 -4px 20px rgba(0,0,0,0.3);
            padding: 10px 10px max(10px, env(safe-area-inset-bottom)) 10px;
          }
          .code-toolbar.focused {
            display: flex;
          }

          /* Mobile Focus Mode: Platz schaffen */
          body.keyboard-open .left-sidebar {
            display: none !important;
          }
          body.keyboard-open .main-content-header {
            display: none !important;
          }
          body.keyboard-open .playground-container {
             height: 100% !important; /* Vollen verbleibenden Platz nutzen */
             padding: 10px !important;
          }
          /* Output etwas kleiner machen, damit man den Code sieht */
          body.keyboard-open .playground-output {
             height: 100px !important;
             flex: 0 0 auto; /* Nicht wachsen, feste Höhe */
          }
        }
      `}</style>
      <div className="main-content-header" style={{ position: 'sticky', top: '20px', zIndex: 10, backgroundColor: '#1cb0f6', borderBottom: '4px solid #148ac2', borderRadius: '16px', color: 'white', marginBottom: '20px', marginTop: '20px' }}>
        <h2>Playground</h2>
        <span>Experimentiere mit Code</span>
      </div>
      
      <div className="playground-container" style={{ padding: '20px', height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div className="widget-card" style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '0', overflow: 'hidden' }}>
          <div style={{ background: '#282c34', padding: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#abb2bf', fontFamily: 'monospace' }}>{activeCourse === 'Python' ? 'main.py' : activeCourse === 'HTML' ? 'index.html' : activeCourse === 'Kotlin' ? 'main.kt' : 'main.js'}</span>
            <button onClick={runCode} disabled={isPyodideLoading || isRunning} style={{
              background: isPyodideLoading || isRunning ? '#444' : '#58cc02', 
              border: 'none', borderRadius: '8px', padding: '5px 15px', 
              color: 'white', fontWeight: 'bold', cursor: (isPyodideLoading || isRunning) ? 'wait' : 'pointer', 
              display: 'flex', alignItems: 'center', gap: '5px', transition: 'background 0.2s'
            }}>
              {isPyodideLoading || isRunning ? <Loader2 size={16} className="spin" /> : <Play size={16} fill="white" />} 
              {isPyodideLoading ? 'Lade...' : isRunning ? 'Läuft...' : 'Run'}
            </button>
          </div>
          <div style={{ position: 'relative', flex: 1, overflow: 'hidden', backgroundColor: '#1e1e1e' }}>
            {/* Highlight Layer */}
            <pre
              ref={preRef}
              aria-hidden="true"
              style={{
                margin: 0,
                padding: '15px',
                fontFamily: 'monospace',
                fontSize: '16px',
                lineHeight: '1.5',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                overflow: 'hidden',
                color: '#abb2bf'
              }}
            >
              {highlightCode(code)}
              <br /> {/* Extra break for scrolling */}
            </pre>
            {/* Input Layer */}
            <textarea 
              ref={textareaRef}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onScroll={handleScroll}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className="no-scrollbar"
              style={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'transparent', 
                color: 'transparent', 
                caretColor: 'white',
                border: 'none', 
                padding: '15px', 
                fontFamily: 'monospace', 
                fontSize: '16px', 
                lineHeight: '1.5',
                resize: 'none', 
                outline: 'none',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                overflow: 'auto'
              }}
              spellCheck="false"
            />
          </div>
          <div className={`code-toolbar no-scrollbar ${isFocused ? 'focused' : ''}`}>
            {['{', '}', '(', ')', '[', ']', '=', ';', ':'].map(char => (
              <button 
                key={char}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { if (navigator.vibrate) navigator.vibrate(10); insertChar(char); }}
                style={{
                  background: '#333', color: '#fff', border: '1px solid #444', borderRadius: '6px', 
                  minWidth: '40px', height: '40px', fontSize: '1.2rem', fontFamily: 'monospace', 
                  cursor: 'pointer', flexShrink: 0
                }}
              >{char}</button>
            ))}
          </div>
        </div>
        
        <div className="widget-card no-scrollbar playground-output" style={{ height: '150px', background: activeCourse === 'HTML' ? 'white' : '#1e1e1e', color: activeCourse === 'HTML' ? 'black' : 'white', fontFamily: 'monospace', padding: activeCourse === 'HTML' ? 0 : '15px', overflow: 'hidden' }}>
          {activeCourse === 'HTML' ? (
             <iframe title="preview" srcDoc={output} style={{ width: '100%', height: '100%', border: 'none' }} sandbox="allow-scripts" />
          ) : (
             <div style={{ height: '100%', overflow: 'auto' }}>
               <div style={{ color: '#5c6370', marginBottom: '5px' }}>// Output</div>
               <pre style={{ margin: 0 }}>{output}</pre>
             </div>
          )}
        </div>
      </div>
    </main>
  );
}

export default Playground;